/**
 * Vocabulario de la herida quirúrgica (2026-09-28).
 *
 * La guía «mis pasos» del paciente y el PDF del postoperatorio deciden si la
 * cirugía deja herida y destacan los productos que solo se usan con ella.
 * Qué procedimientos la dejan, cómo los escribe el equipo y qué productos son
 * esos es de CADA CLÍNICA: vive en la base (GET/PUT /api/vocabulario-herida)
 * y empieza vacío. Mientras esté vacío la guía no afirma nada sobre la herida
 * ni destaca ningún producto.
 *
 * Una palabra o frase por línea; sin importar tildes ni mayúsculas.
 * «palabra*» vale para todo lo que empiece así y «a & b» pide las dos.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

export const URL_VOCABULARIO_HERIDA = '/api/vocabulario-herida';

const CAMPOS = [
  { clave: 'con_herida', titulo: 'Dejan herida',
    ayuda: 'Procedimientos (y abreviaturas del equipo) que cortan piel: con cualquiera, hay herida.' },
  { clave: 'sin_herida', titulo: 'No dejan herida',
    ayuda: 'Si la nota no dice ninguno de los de arriba y sí uno de estos, no hay herida.' },
  { clave: 'productos_solo_herida', titulo: 'Productos que solo se usan con herida',
    ayuda: 'En la pauta del paciente se destacan cuando no está claro que haya herida.' },
];

const aTexto = (lista) => (lista || []).join('\n');
const aLista = (texto) => String(texto || '').split('\n').map((l) => l.trim()).filter(Boolean);

export default function VocabularioHeridaDialog({ open, onOpenChange }) {
  const [textos, setTextos] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let vivo = true;
    setTextos(null);
    api.get(URL_VOCABULARIO_HERIDA)
      .then((r) => { if (vivo) setTextos(Object.fromEntries(CAMPOS.map(({ clave }) => [clave, aTexto(r.data?.[clave])]))); })
      .catch((e) => {
        if (!vivo) return;
        toast.error(e?.response?.data?.detail || 'No se pudo leer el vocabulario');
        onOpenChange(false);
      });
    return () => { vivo = false; };
  }, [open, onOpenChange]);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.put(URL_VOCABULARIO_HERIDA,
        Object.fromEntries(CAMPOS.map(({ clave }) => [clave, aLista(textos[clave])])));
      toast.success('Vocabulario guardado');
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="vocabulario-herida-dialog">
        <DialogHeader><DialogTitle>Herida quirúrgica y productos</DialogTitle></DialogHeader>
        {!textos ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#2E6E5E]" /></div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Lo usa la guía del paciente para saber si su cirugía deja herida. Una palabra o frase por línea, sin
              importar tildes ni mayúsculas; «palabra*» vale para todo lo que empiece así y «a &amp; b» pide las dos.
              Vacío, la guía no afirma nada sobre la herida ni destaca productos.
            </p>
            {CAMPOS.map(({ clave, titulo, ayuda }) => (
              <div key={clave}>
                <Label>{titulo}</Label>
                <Textarea rows={4} value={textos[clave]} className="mt-1.5 font-mono text-sm"
                  onChange={(e) => setTextos((t) => ({ ...t, [clave]: e.target.value }))}
                  data-testid={`vocabulario-${clave}`} />
                <p className="text-[11px] text-muted-foreground mt-1">{ayuda}</p>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={guardar} disabled={!textos || guardando} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="vocabulario-guardar">
            {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
